import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { FormFieldConfig, ValidationRule, VarType } from '../models/form.models';
import { isEqual } from 'date-fns';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-form-component',
  templateUrl: './form-component.component.html',
  styleUrls: ['./form-component.component.scss']
})
export class FormComponentComponent implements OnInit, OnChanges {
  // Exponer VarType para usarlo en el template
  VarType = VarType;
  dynamicOptions: { [key: string]: any[] } = {};
  @Input() fieldsConfig: FormFieldConfig[] = [];
  @Input() submitButtonText = 'Guardar';
  @Input() resetButtonText = 'Limpiar';
  @Input() showResetButton = true;
  @Input() showBotons = false;
  @Input() formLayout: 'horizontal' | 'vertical' = 'vertical';
  @Input() formGroup!: FormGroup;
  @Input() canEdit = true;

  @Output() formSubmit = new EventEmitter<any>();
  @Output() formChange = new EventEmitter<any>();
  @Output() formReset = new EventEmitter<void>();
  isLoadingSelect: { [key: string]: boolean } = {};


  private destroy$ = new Subject<void>();

  constructor(private fb: FormBuilder,
    private cdRef: ChangeDetectorRef,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadDynamicOptions();
    this.normalizeFieldWidths();
    this.setupFormListeners();
    this.cdRef.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fieldsConfig'] && !changes['fieldsConfig'].firstChange) {
      if (!isEqual(changes['fieldsConfig'].previousValue, changes['fieldsConfig'].currentValue)) {
        this.loadDynamicOptions();
        this.normalizeFieldWidths();
        this.setupFormListeners();
      }
    }

    if (changes['canEdit']) {
      this.updateFieldsDisabledState();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cdRef.detach();
  }

  private updateFieldsDisabledState(): void {
    if (!this.formGroup) return;

    this.fieldsConfig.forEach(field => {
      const control = this.formGroup.get(field.fieldName);
      if (control) {
        if (this.isFieldDisabled(field)) {
          control.disable({ emitEvent: false });
        } else {
          control.enable({ emitEvent: false });
        }
      }
    });
  }

  private setupFormListeners(): void {
    if (this.formGroup) {
      this.formGroup.valueChanges
        .pipe(
          takeUntil(this.destroy$),
          debounceTime(300)
        )
        .subscribe(values => {
          const visibleValues = this.getVisibleFormValues();
          this.formChange.emit(visibleValues);
        });
    }
  }

  private loadDynamicOptions(): void {
    this.dynamicOptions = {};

    this.fieldsConfig.forEach(field => {
      if (field.dataType.equals(VarType.SELECTDINAMIC) && field.service && field.service.findAll) {
        this.loadOptionsForField(field);
      }
    });
  }

  private loadOptionsForField(field: FormFieldConfig): void {
    this.isLoadingSelect[field.fieldName] = true;

    field.service.findAll(field.filter || {}).subscribe({
      next: (data: any[]) => {
        this.dynamicOptions[field.fieldName] = data;
        this.isLoadingSelect[field.fieldName] = false;
      },
      error: () => {
        this.dynamicOptions[field.fieldName] = [];
        this.isLoadingSelect[field.fieldName] = false;
      }
    });
  }



  isFieldRequired(field: FormFieldConfig): boolean {
    return field.validations?.some(v => v.type === 'required') || field.isRequired || false;
  }

  isFieldDisabled(field: FormFieldConfig): boolean {
    if (field.isDisabled !== undefined) {
      return field.isDisabled;
    }
    return !this.canEdit;
  }

  private normalizeFieldWidths(): void {
    this.fieldsConfig.forEach(field => {
      if (!field.width) {
        field.width = 100;
      }

      if (!field.cssClass) {
        field.cssClass = '';
      }

      if (field.width >= 98 && field.width <= 100) {
        field.cssClass += ' width-100';
      } else if (field.width >= 48 && field.width <= 52) {
        field.cssClass += ' width-50';
      } else if (field.width >= 31 && field.width <= 35) {
        field.cssClass += ' width-33';
      } else if (field.width >= 23 && field.width <= 27) {
        field.cssClass += ' width-25';
      }
    });
  }

  private getVisibleFormValues(): any {
    const result: any = {};
    this.fieldsConfig
      .filter(field => field.isVisible)
      .forEach(field => {
        result[field.fieldName] = this.formGroup.get(field.fieldName)?.value;
      });
    return result;
  }

  private getValidators(validations: ValidationRule[]): any[] {
    const validatorsList: any[] = [];

    validations?.forEach(validation => {
      switch (validation.type) {
        case 'required':
          validatorsList.push(Validators.required);
          break;
        case 'minLength':
          validatorsList.push(Validators.minLength(validation.value));
          break;
        case 'maxLength':
          validatorsList.push(Validators.maxLength(validation.value));
          break;
        case 'pattern':
          validatorsList.push(Validators.pattern(validation.value));
          break;
        case 'email':
          validatorsList.push(Validators.email);
          break;
        case 'min':
          validatorsList.push(Validators.min(validation.value));
          break;
        case 'max':
          validatorsList.push(Validators.max(validation.value));
          break;
        case 'custom':
          if (validation.validator) {
            validatorsList.push(validation.validator);
          }
          break;
      }
    });

    return validatorsList;
  }

  resetForm(): void {
    this.formGroup.reset();

    this.fieldsConfig.forEach(field => {
      if (field.isVisible && this.formGroup.get(field.fieldName)) {
        const control = this.formGroup.get(field.fieldName);
        control?.setValue(field.initialValue);
        if (this.isFieldDisabled(field)) {
          control?.disable({ emitEvent: false });
        }
      }
    });

    this.formReset.emit();
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();

      if ((control as FormGroup).controls) {
        this.markFormGroupTouched(control as FormGroup);
      }
    });
  }

  hasError(fieldName: string, errorType: string): boolean {
    const control = this.formGroup.get(fieldName);
    return control ? control.hasError(errorType) && (control.touched || control.dirty) : false;
  }

  getErrorMessages(fieldName: string): string[] {
    const control = this.formGroup.get(fieldName);
    if (!control || !control.errors || !(control.touched || control.dirty)) {
      return [];
    }

    const field = this.fieldsConfig.find(f => f.fieldName === fieldName);
    if (!field || !field.validations) {
      return [];
    }

    return field.validations
      .filter(validation => control.hasError(validation.type))
      .map(validation => validation.message);
  }

  onSubmit(): void {
    if (this.formGroup.valid && this.canEdit) {
      const visibleValues = this.getVisibleFormValues();
      this.formSubmit.emit(visibleValues);
    } else {
      this.markFormGroupTouched(this.formGroup);
    }
  }
  addChip(event: MatChipInputEvent, fieldName: string): void {
    const input = event.input;
    const value = (event.value || '').trim();

    // Add our item
    if (value) {
      const currentValue = (this.formGroup?.get(fieldName)?.value as any[] || []);
      this.formGroup?.get(fieldName)?.setValue([...currentValue, value]);
    }

    input.value = '';
  }

  removeChip(fieldName: string, item: string): void {
    const currentValue = (this.formGroup?.get(fieldName)?.value as any[] || []);
    const index = currentValue.indexOf(item);

    if (index >= 0) {
      const newValue = [...currentValue];
      newValue.splice(index, 1);
      this.formGroup?.get(fieldName)?.setValue(newValue);
    }
  }
  openCreateDialog(field: FormFieldConfig): void {
    const dialogRef = this.dialog.open(field.openDialogComponent, {
      width: '500px',
      data: {}
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        const valueField = field.valueField || 'id';
        const labelField = field.labelField || 'nombre';

        const currentOptions = this.dynamicOptions[field.fieldName] || [];
        const exists = currentOptions.some(opt => opt[valueField] === result[valueField]);

        if (!exists) {
          this.dynamicOptions[field.fieldName] = [...currentOptions, result];
          this.loadDynamicOptions();
          this.getSelectOptions(field);
          if (this.dynamicOptions[field.fieldName]?.length > 0) {
            const lastOption = this.dynamicOptions[field.fieldName][this.dynamicOptions[field.fieldName].length - 1];
            if (lastOption && lastOption[valueField]) {
              this.formGroup.get(field.fieldName)?.setValue(lastOption[valueField]);
            }
          }
          return;
        }

        this.formGroup.get(field.fieldName)?.setValue(result[valueField]);
      }
    });
  }


  getSelectOptions(field: FormFieldConfig): any[] {
    return field.service ? (this.dynamicOptions[field.fieldName] || []) : (field.options || []);
  }

  getSelectValue(field: FormFieldConfig, item: any): any {
    return field.service ? item[field.valueField || 'id'] : item.value;
  }

  getSelectLabel(field: FormFieldConfig, item: any): string {
    return field.service ? item[field.labelField || 'nombre'] : item.label;
  }

}
